import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Lock, Sparkles, User } from 'lucide-react';
import { AUDIENCES, type AudienceMeta } from '@shared/constants/audiences';
import { useActiveAudience } from '../../hooks/useActiveAudience';
import { useAudienceStore } from '../../stores/audience';
import { AUDIENCE_ICON_MAP, AUDIENCE_TONE_BG } from '../../lib/audience-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

const TONE_BADGE: Record<AudienceMeta['tone'], string> = {
  brand: 'text-brand-700',
  lavender: 'text-lavender-700',
  sage: 'text-sage-500',
  'lavender-700': 'text-lavender-800',
  'brand-700': 'text-brand-800',
  accent: 'text-accent-800',
};

export function AudienceDropdown() {
  const [open, setOpen] = useState(false);
  const active = useActiveAudience();
  const setAudience = useAudienceStore((s) => s.setAudience);
  const lockedAudienceId = useAudienceStore((s) => s.lockedAudienceId);
  const isLocked = !!lockedAudienceId;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Sélection : stocke dans le store (synchronise cookie + localStorage),
  // PUIS navigue vers la page de l'audience. La navigation pleine page garantit
  // que Header/Footer/CTA/SSR sont tous cohérents (URL = source de vérité).
  const handleSelect = (a: AudienceMeta) => {
    setAudience(a.id);
    setOpen(false);
    if (typeof window !== 'undefined' && window.location.pathname !== a.href) {
      window.location.href = a.href;
    }
  };

  const ActiveIcon = active ? AUDIENCE_ICON_MAP[active.iconName] : User;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !isLocked && setOpen((v) => !v)}
        aria-expanded={isLocked ? undefined : open}
        aria-haspopup={isLocked ? undefined : 'listbox'}
        aria-label={
          isLocked
            ? `Profil verrouillé : ${active?.label ?? lockedAudienceId}`
            : 'Sélectionner mon profil de navigation'
        }
        title={isLocked ? 'Le profil est verrouillé sur cette page outil' : undefined}
        className={cn(
          'inline-flex min-h-10 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium shadow-card transition-all duration-150 ease-out',
          active
            ? 'border-stone-200 bg-paper text-ink hover:bg-stone-50 hover:border-stone-300'
            : 'border-dashed border-stone-300 bg-stone-50/60 text-stone-600 hover:bg-stone-100 hover:border-stone-400',
          open && 'bg-stone-50 border-stone-300',
          isLocked && 'cursor-default opacity-80',
        )}
      >
        {active ? (
          <>
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-transform duration-150 ease-out',
                AUDIENCE_TONE_BG[active.tone],
              )}
            >
              <ActiveIcon className="h-3 w-3" strokeWidth={2} />
            </span>
            <span className={cn('hidden sm:block font-semibold', TONE_BADGE[active.tone])}>
              {active.shortLabel}
            </span>
          </>
        ) : (
          <>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-stone-200/60 text-stone-500">
              <User className="h-3 w-3" strokeWidth={2} />
            </span>
            <span className="hidden sm:block">Mon profil</span>
          </>
        )}
        {isLocked ? (
          <Lock className="h-3 w-3 text-stone-400" />
        ) : (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-stone-400 transition-transform duration-150 ease-out',
              open && 'rotate-180',
            )}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            aria-label="Profils de navigation"
            initial={{ opacity: 0, y: 4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: easeStripe }}
            className="absolute right-0 top-full z-50 mt-1.5 w-[280px] max-w-[calc(100vw-2rem)] origin-top-right overflow-hidden rounded-xl border border-stone-200 bg-paper shadow-floating"
          >
            <div className="border-b border-stone-100 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                Mon profil de navigation
              </p>
              <p className="mt-0.5 text-[11px] text-stone-500">
                Sélectionner un profil ouvre l'espace dédié.
              </p>
            </div>

            <div className="p-1.5" role="group">
              {AUDIENCES.map((a) => {
                const Icon = AUDIENCE_ICON_MAP[a.iconName];
                const isActive = active?.id === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(a)}
                    className={cn(
                      'group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors duration-100 ease-out',
                      isActive
                        ? 'bg-stone-100 text-ink'
                        : 'text-stone-700 hover:bg-stone-50 hover:text-ink',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-transform duration-100 ease-out group-hover:scale-105',
                        AUDIENCE_TONE_BG[a.tone],
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        {a.label}
                        {isActive && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-600">
                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                            actif
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-[11px] font-normal text-stone-500">
                        {a.tagline}
                      </span>
                    </span>
                    {a.highlight && (
                      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-ink px-1.5 py-0.5 text-[9px] font-semibold uppercase text-ivory-50">
                        <Sparkles className="h-2.5 w-2.5 text-accent-400" />
                        Phare
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {active && (
              <div className="border-t border-stone-100 px-3 py-2.5">
                <a
                  href={active.href}
                  className={cn(
                    'inline-flex items-center gap-1 text-[11px] font-semibold underline-offset-2 hover:underline',
                    TONE_BADGE[active.tone],
                  )}
                >
                  Voir mon espace {active.shortLabel} →
                </a>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
